import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode, type RefObject } from 'react';
import type { Series, SeriesPoint } from './profiler.types';
import { SPIKE_THRESHOLD_MS, TICK_BUDGET_MS } from './profiler.health';
import { areaPath, columnPath, labelSide, linePath, nearest, niceTicks, runs, scale, tickDecimals, timeTicks } from './profiler.chart';
import { fmtBytes, fmtClock, fmtDay, fmtMs, fmtNumber, fmtTps, plural, stepLabel } from './profiler.format';
import { cx } from './ProfilerParts';
import s from './Profiler.module.scss';

// ============================================================
// Profileur : les deux courbes de la page d'un serveur
// ============================================================
// Durée moyenne d'un tick (ligne) et nombre de ticks trop longs (colonnes), sur le même axe du temps,
// l'une sous l'autre : deux mesures d'échelles différentes ne partagent jamais un même cadre.
//
// - une seule série par cadre, donc une seule teinte et pas de légende : le titre dit ce qui est tracé ;
// - l'axe vertical part toujours de zéro ;
// - une mesure qui manque coupe le tracé, elle n'est ni reliée ni ramenée à zéro ;
// - le réticule se cale sur la mesure la plus proche et montre tous ses chiffres, à la souris comme
//   au clavier (flèches gauche et droite) ; les mêmes chiffres sont dans « Voir les chiffres ».
// ============================================================

const HEIGHT = 220;
const COLUMNS_HEIGHT = 150;
const MARGIN = { left: 48, right: 14, top: 14, bottom: 26 };

/** Largeur réelle du cadre, pour dessiner au pixel (texte net, colonnes d'épaisseur exacte). */
function useWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver((entries) => setWidth(Math.round(entries[0].contentRect.width)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** « 14:02 » pour un point d'une minute, « 14:00 à 14:05 » pour une tranche ; le jour devant au-delà de 24 heures. */
function bucketLabel(t: number, stepSeconds: number, withDay: boolean): string {
  const day = withDay ? `${fmtDay(t)} · ` : '';
  if (stepSeconds <= 60) return `${day}${fmtClock(t)}`;
  return `${day}${fmtClock(t)} à ${fmtClock(t + stepSeconds * 1000)}`;
}

interface Frame {
  width: number;
  height: number;
  plotW: number;
  plotH: number;
  /** Abscisse du milieu de la tranche qui commence à `t`. */
  x: (t: number) => number;
}

function frameOf(series: Series, width: number, height: number): Frame {
  const plotW = Math.max(0, width - MARGIN.left - MARGIN.right);
  const plotH = height - MARGIN.top - MARGIN.bottom;
  const half = (series.stepSeconds * 1000) / 2;
  return { width, height, plotW, plotH, x: (t) => MARGIN.left + scale(t + half, series.from, series.to, plotW) };
}

/** Axes communs : graduations horizontales, ligne de base, heures locales. */
function Axes({ frame, series, yTicks, yMax, unit }: { frame: Frame; series: Series; yTicks: number[]; yMax: number; unit: (v: number) => string }) {
  const xTicks = useMemo(() => timeTicks(series.from, series.to), [series.from, series.to]);
  const baseY = MARGIN.top + frame.plotH;
  return (
    <g>
      {yTicks.map((v) => {
        const y = baseY - scale(v, 0, yMax, frame.plotH);
        return (
          <g key={v}>
            <line className={v === 0 ? s.vizAxis : s.vizGrid} x1={MARGIN.left} x2={MARGIN.left + frame.plotW} y1={y} y2={y} />
            <text className={s.vizTick} x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle">{unit(v)}</text>
          </g>
        );
      })}
      {xTicks.map((tick) => {
        const x = MARGIN.left + scale(tick.t, series.from, series.to, frame.plotW);
        return (
          <text key={tick.t} className={cx(s.vizTick, tick.midnight && s.vizTickStrong)} x={x} y={baseY + 17} textAnchor="middle">
            {tick.midnight ? fmtDay(tick.t) : fmtClock(tick.t)}
          </text>
        );
      })}
    </g>
  );
}

/** Infobulle du réticule : la valeur d'abord, en gras, son nom ensuite. */
function Tooltip({ point, series, frame }: { point: SeriesPoint; series: Series; frame: Frame }) {
  const x = frame.x(point.t);
  const right = x > frame.width * 0.6;
  const rows: [string, string][] = point.ticks > 0
    ? [
      [fmtMs(point.avgMs), "durée moyenne d'un tick"],
      [fmtMs(point.maxMs), 'tick le plus long'],
      [fmtTps(point.tps), 'ticks par seconde'],
      [fmtBytes(point.bytesPerTick), 'mémoire par tick'],
      [fmtNumber(point.spikes), point.spikes > 1 ? 'ticks trop longs' : 'tick trop long'],
    ]
    : [['Aucun', 'tick fini sur cette tranche']];
  return (
    <div className={s.tooltip} style={right ? { right: frame.width - x + 12 } : { left: x + 12 }} role="status">
      <div className={s.tooltipTitle}>{bucketLabel(point.t, series.stepSeconds, series.hours > 24)}</div>
      {rows.map(([value, label]) => (
        <div key={label} className={s.tooltipRow}><strong>{value}</strong><span>{label}</span></div>
      ))}
      {point.inProgressMs >= 1000 && (
        <div className={s.tooltipNote}>Monde figé : un tick en cours depuis {fmtMs(point.inProgressMs)}.</div>
      )}
    </div>
  );
}

interface ChartProps {
  series: Series;
  /** Tranche survolée (son début), partagée par les deux courbes. */
  hover: number | null;
  onHover: (t: number | null) => void;
}

/** Cadre interactif commun : survol, clavier, réticule. `children` dessine les marques. */
function Plot({ series, hover, onHover, height, label, children }: ChartProps & {
  height: number;
  label: string;
  children: (frame: Frame, hovered: SeriesPoint | null) => ReactNode;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState(false);
  const frame = frameOf(series, width, height);
  const hovered = hover == null ? null : series.points.find((p) => p.t === hover) ?? null;
  const half = (series.stepSeconds * 1000) / 2;

  const onMove = (e: PointerEvent<SVGRectElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const t = series.from + ((e.clientX - box.left) / Math.max(1, box.width)) * (series.to - series.from);
    const p = nearest(series.points, t - half);
    setActive(true);
    onHover(p ? p.t : null);
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (series.points.length === 0) return;
    if (e.key === 'Escape') { onHover(null); return; }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const i = hovered ? series.points.indexOf(hovered) : e.key === 'ArrowLeft' ? series.points.length : -1;
    const next = Math.max(0, Math.min(series.points.length - 1, i + (e.key === 'ArrowLeft' ? -1 : 1)));
    setActive(true);
    onHover(series.points[next].t);
  };

  return (
    <div ref={ref} className={s.plot} style={{ height }} tabIndex={0} role="group" aria-label={label}
      onKeyDown={onKey} onBlur={() => { setActive(false); onHover(null); }}>
      {width > 0 && (
        <svg width={width} height={height} className={s.viz} aria-hidden>
          {children(frame, hovered)}
          {hovered && (
            <line className={s.vizCrosshair} x1={frame.x(hovered.t)} x2={frame.x(hovered.t)} y1={MARGIN.top} y2={MARGIN.top + frame.plotH} />
          )}
          <rect x={MARGIN.left} y={MARGIN.top} width={frame.plotW} height={frame.plotH} fill="transparent"
            onPointerMove={onMove} onPointerLeave={() => { setActive(false); onHover(null); }} />
        </svg>
      )}
      {active && hovered && width > 0 && <Tooltip point={hovered} series={series} frame={frame} />}
    </div>
  );
}

/**
 * Durée moyenne d'un tick au fil du temps. La ligne du budget (50 ms) est tracée dès que les mesures
 * s'en approchent ; tant qu'elles en restent loin, le cadre se resserre sur elles pour que leur
 * évolution se lise, et le sous-titre de la carte rappelle où est le budget.
 */
export function TickChart(props: ChartProps) {
  const { series } = props;
  const stepMs = series.stepSeconds * 1000;
  const dataMax = series.points.reduce((m, p) => Math.max(m, p.avgMs ?? 0), 0);
  const showBudget = budgetInFrame(series);
  const yTicks = niceTicks(showBudget ? Math.max(dataMax * 1.1, TICK_BUDGET_MS * 1.1) : Math.max(dataMax * 1.15, 4), 4);
  const yMax = yTicks[yTicks.length - 1];
  const segments = useMemo(() => runs(series.points, (p) => p.avgMs, stepMs), [series.points, stepMs]);
  // Le nom du budget s'écrit du côté où la courbe en est loin, pour ne jamais la chevaucher.
  const budgetSide = useMemo(() => labelSide(series.points.map((p) => p.avgMs), TICK_BUDGET_MS), [series.points]);

  return (
    <Plot {...props} height={HEIGHT} label={`Durée moyenne d'un tick, ${stepLabel(series.stepSeconds)}. Flèches gauche et droite pour lire chaque mesure.`}>
      {(frame, hovered) => {
        const baseY = MARGIN.top + frame.plotH;
        const y = (v: number) => baseY - scale(v, 0, yMax, frame.plotH);
        return (
          <>
            <Axes frame={frame} series={series} yTicks={yTicks} yMax={yMax} unit={(v) => `${fmtNumber(v, tickDecimals(yTicks))} ms`} />
            {showBudget && (
              <g>
                <line className={s.vizBudget} x1={MARGIN.left} x2={MARGIN.left + frame.plotW} y1={y(TICK_BUDGET_MS)} y2={y(TICK_BUDGET_MS)} />
                <text className={s.vizBudgetLabel} y={y(TICK_BUDGET_MS) - 6}
                  x={budgetSide === 'left' ? MARGIN.left + 4 : MARGIN.left + frame.plotW - 4} textAnchor={budgetSide === 'left' ? 'start' : 'end'}>
                  Budget d'un tick : {TICK_BUDGET_MS} ms
                </text>
              </g>
            )}
            {segments.map((segment) => {
              const xy = segment.map((p) => ({ x: frame.x(p.t), y: y(p.avgMs ?? 0) }));
              return xy.length === 1
                ? <circle key={segment[0].t} className={s.vizDot} cx={xy[0].x} cy={xy[0].y} r={3} />
                : (
                  <g key={segment[0].t}>
                    <path className={s.vizArea} d={areaPath(xy, baseY)} />
                    <path className={s.vizLine} d={linePath(xy)} />
                  </g>
                );
            })}
            {hovered && hovered.avgMs != null && (
              <circle className={s.vizMarker} cx={frame.x(hovered.t)} cy={y(hovered.avgMs)} r={5} />
            )}
          </>
        );
      }}
    </Plot>
  );
}

/** Le budget de 50 ms entre-t-il dans le cadre de la courbe ? (dès que la moyenne dépasse 20 ms) */
export function budgetInFrame(series: Series): boolean {
  return series.points.some((p) => (p.avgMs ?? 0) >= TICK_BUDGET_MS * 0.4);
}

/** Nombre de ticks trop longs par tranche : une colonne par tranche qui en compte, rien ailleurs. */
export function SpikeColumns(props: ChartProps) {
  const { series } = props;
  const max = series.points.reduce((m, p) => Math.max(m, p.spikes), 0);
  const yTicks = niceTicks(Math.max(max, 2), 3);
  const yMax = yTicks[yTicks.length - 1];
  return (
    <Plot {...props} height={COLUMNS_HEIGHT} label={`Nombre de ticks de plus de ${SPIKE_THRESHOLD_MS} ms, ${stepLabel(series.stepSeconds)}. Flèches gauche et droite pour lire chaque mesure.`}>
      {(frame, hovered) => {
        const baseY = MARGIN.top + frame.plotH;
        // Largeur d'une tranche à l'écran ; la colonne laisse 2 px d'air de chaque côté et ne dépasse jamais 24 px.
        const slot = frame.plotW * ((series.stepSeconds * 1000) / Math.max(1, series.to - series.from));
        const w = Math.max(2, Math.min(24, slot - 2));
        return (
          <>
            <Axes frame={frame} series={series} yTicks={yTicks} yMax={yMax} unit={(v) => fmtNumber(v)} />
            {series.points.filter((p) => p.spikes > 0).map((p) => (
              <path key={p.t} className={cx(s.vizColumn, hovered?.t === p.t && s.vizColumnHover)}
                d={columnPath(frame.x(p.t) - w / 2, w, baseY - scale(p.spikes, 0, yMax, frame.plotH), baseY, Math.min(4, w / 2))} />
            ))}
          </>
        );
      }}
    </Plot>
  );
}

/** Les chiffres des deux courbes en tableau : la plus récente tranche d'abord. */
export function SeriesTable({ series }: { series: Series }) {
  const rows = [...series.points].reverse();
  return (
    <details className={s.tableView}>
      <summary>Voir les chiffres ({plural(rows.length, 'mesure', 'mesures')})</summary>
      <div className={s.tableScroll}>
        <table className={s.dataTable}>
          <thead>
            <tr>
              <th scope="col">Heure</th>
              <th scope="col" className={s.num}>Durée moyenne</th>
              <th scope="col" className={s.num}>Tick le plus long</th>
              <th scope="col" className={s.num}>Ticks par seconde</th>
              <th scope="col" className={s.num}>Mémoire par tick</th>
              <th scope="col" className={s.num}>Ticks trop longs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.t}>
                <th scope="row">{bucketLabel(p.t, series.stepSeconds, true)}</th>
                <td className={s.num}>{p.ticks > 0 ? fmtMs(p.avgMs) : 'aucun tick fini'}</td>
                <td className={s.num}>{fmtMs(p.maxMs)}</td>
                <td className={s.num}>{fmtTps(p.tps)}</td>
                <td className={s.num}>{fmtBytes(p.bytesPerTick)}</td>
                <td className={s.num}>{fmtNumber(p.spikes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
